import _ from "lodash";
import React, { useRef } from "react";
import { connect } from "react-redux";
import { useI18n } from "Hooks";

import { Tooltip, IconButton } from "@toddle-edu/ds-web";
import { MinusCircleOutlined } from "@toddle-edu/ds-icons";
import { AvatarWithAccessPoint } from "MessagingV2/components/ThreadList/Avatar";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getUserListEnhancer } from "MessagingV2/modules/MessagingEnhancers";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import { compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import Loading from "UIComponents/Loading";
import { useHandleScroll } from "Utils/CustomHooks";

import {
  SUB_TEXT_LENGTH,
  CHAT_STATUS_ENUM,
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { isIdEqual } from "Utils";

import classNames from "classnames";
import classes from "./UserTypeList.scss";

const UserTypeCard = ({
  onRemoveClick,
  avatarId,
  mainText,
  subText,
  profileImage,
  isRemovable,
  userType,
}) => {
  return (
    <div className={classes.userTypeCardContainer}>
      <AvatarWithAccessPoint
        id={avatarId}
        name={mainText}
        url={profileImage}
        userType={userType}
        onlineStatus={false}
      />
      <div className={classes.userInfoContainer}>
        <div className="text-body-1">{mainText}</div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={classNames("text-body-2", classes.userSubTitle)}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      {isRemovable ? (
        <div className={classes.iconButtonContainer}>
          <IconButton
            icon={<MinusCircleOutlined />}
            variant="plain"
            size="medium"
            onClick={onRemoveClick}
            className={classes.removeIcon}
          />
        </div>
      ) : null}
    </div>
  );
};

const UserTypeList = ({
  onRemoveClick,
  customAvatarContainerClassName,
  defaultStudentNameSortPreference,
  defaultStudentNameOrderPreference,
  loggedInUserId,
  loggedInUserType,
  isMemberUpdate,
  isUpdateFlow,
  users: updateFlowUsers,
  userParams,
}) => {
  const { t } = useI18n(["common"]);
  const users = isUpdateFlow
    ? updateFlowUsers
    : _.map(_.get(userParams, "userNodeList", []), "node");
  const containerRef = useRef(null);
  const {
    networkStatus,
    loadMoreUsers = _.noop,
    hasNextPage = false,
    isFetchingMore = false,
  } = userParams || {};

  useHandleScroll({
    networkStatus,
    loadMoreFeed: loadMoreUsers,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  return (
    <div className={classes.userListContainer} ref={containerRef}>
      {_.map(users, user => {
        const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

        const { id, profileImage } = user;

        let name = getFormattedNameForUserType({
          user,
          orderBy: defaultStudentNameSortPreference,
          defaultStudentNameOrderPreference,
          isFormattedNameV2: true,
        });

        if (isCurrentLoggedInUser) {
          name = t("common:you_with_full_name", { fullName: name });
        }
        return (
          <UserTypeCard
            key={id}
            userType={_.get(user, "type")}
            onRemoveClick={() => onRemoveClick({ user })}
            customAvatarContainerClassName={customAvatarContainerClassName}
            avatarId={id}
            mainText={name}
            subText={getSubText({
              loggedInUserRole: loggedInUserType,
              messagedUserObj: user,
              messagedUserRole: _.get(user, "type"),
              t,
              defaultStudentNameSortPreference,
            })}
            profileImage={profileImage}
            isRemovable={isMemberUpdate && !isCurrentLoggedInUser}
          />
        );
      })}
      {isFetchingMore ? <Loading /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const isLoading = _.get(ownProps, "isLoading", false);
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    loggedInUserType,
    loggedInUserId,
    isData: !isLoading,
    isLoading: isLoading,
  };
};

const getVariables = ({ users }) => ({
  includedUserIds: _.map(users, "id"), // todo add memoize from top of parent directly pass only included users ids
  chatStatus: CHAT_STATUS_ENUM.ALL,
  includeInitiatedChatUsers: false,
});

export default compose(
  connect(mapStateToProps),
  getUserListEnhancer({
    userType: STAFF,
    firstFetchCount: 20,
    nextFetchCount: 20,
    skip: ({ userType, isUpdateFlow, users }) =>
      isUpdateFlow || _.isEmpty(users) || !_.isEqual(userType, STAFF),
    getVariables,
  }),
  getUserListEnhancer({
    userType: STUDENT,
    firstFetchCount: 20,
    nextFetchCount: 20,
    skip: ({ userType, isUpdateFlow, users }) =>
      isUpdateFlow || _.isEmpty(users) || !_.isEqual(userType, STUDENT),
    getVariables,
  }),
  getUserListEnhancer({
    userType: PARENT,
    firstFetchCount: 20,
    nextFetchCount: 20,
    skip: ({ userType, isUpdateFlow, users }) =>
      isUpdateFlow || _.isEmpty(users) || !_.isEqual(userType, PARENT),
    getVariables,
  }),
  withLoader
)(UserTypeList);
